import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, Cpu, BellRing, CheckCircle2 } from 'lucide-react';

const CHART_DATA = [
  { day: 'Mon', h: 40 },
  { day: 'Tue', h: 65 },
  { day: 'Wed', h: 90 },
  { day: 'Thu', h: 70 },
  { day: 'Fri', h: 30 },
];

const NODE_STEPS = ['Input', 'Process', 'Output'];

const TRUST_BADGES = [
  '150+ Indian businesses',
  '24/7 coverage',
  'AI-powered ops',
];

const HeroLeft = () => (
  <div className="max-w-xl">
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#1AB6FF]/30 bg-[#1AB6FF]/5 text-xs text-[#1AB6FF] font-medium mb-6">
      <span className="w-1.5 h-1.5 rounded-full bg-[#1AB6FF] dot-pulse" />
      Made in India &middot; Built for the world
    </div>
    <h1 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
      Build <span className="gradient-text">Smarter.</span>
      <br />
      Scale <span className="gradient-text">Faster.</span>
    </h1>
    <p className="mt-6 text-white/65 text-lg leading-relaxed">
      We help Indian businesses build scalable backend operations through elite remote teams,
      AI systems, automation and operational infrastructure.
    </p>

    <div className="mt-10 flex flex-wrap gap-4">
      <Link to="/contact" className="btn-cyan rounded-full px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2">
        Book Strategy Call <ArrowRight size={16} />
      </Link>
      <Link to="/services" className="btn-outline-cyan rounded-full px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2">
        Build My Team <ArrowRight size={16} />
      </Link>
    </div>

    <div className="mt-10 flex items-center gap-6 text-xs text-white/55">
      {TRUST_BADGES.map((b) => (
        <span key={b} className="flex items-center gap-2">
          <CheckCircle2 size={14} className="text-[#1AB6FF]" /> {b}
        </span>
      ))}
    </div>
  </div>
);

const NodeEditorCard = () => (
  <div className="absolute top-0 left-0 w-72 card-tech rounded-2xl p-5 float-anim" style={{ animationDelay: '0s' }}>
    <p className="font-display font-semibold text-white mb-4">AI Node Editor</p>
    <div className="flex items-center gap-2">
      {NODE_STEPS.map((label, idx) => (
        <React.Fragment key={label}>
          <div className="flex-1 aspect-square rounded-lg bg-[#0a1018] border border-[#1AB6FF]/25 flex flex-col items-center justify-center gap-1">
            <Cpu size={20} className="text-[#1AB6FF]" />
            <span className="text-[10px] text-white/70">{label}</span>
          </div>
          {idx < NODE_STEPS.length - 1 && <ArrowRight size={14} className="text-[#1AB6FF]" />}
        </React.Fragment>
      ))}
    </div>
  </div>
);

const PerformanceCard = () => (
  <div className="absolute top-0 right-0 w-72 card-tech rounded-2xl p-5 float-anim" style={{ animationDelay: '1.5s' }}>
    <p className="font-display font-semibold text-white mb-4">Performance Chart</p>
    <div className="flex items-end justify-between gap-2 h-28">
      {CHART_DATA.map((d) => (
        <div key={d.day} className="flex-1 h-full flex flex-col justify-end items-center gap-1">
          <div
            className="w-full rounded-t-md bg-gradient-to-t from-[#1AB6FF]/40 to-[#1AB6FF]"
            style={{ height: `${d.h}%` }}
          />
          <span className="text-[10px] text-white/55">{d.day}</span>
        </div>
      ))}
    </div>
  </div>
);

const AlertsCard = () => (
  <div className="absolute bottom-0 left-4 w-72 card-tech rounded-2xl p-5 float-anim" style={{ animationDelay: '0.7s' }}>
    <p className="font-display font-semibold text-white mb-4">Alerts Notification</p>
    <div className="flex items-center justify-center mb-3">
      <div className="w-14 h-14 rounded-full border-2 border-[#1AB6FF]/40 flex items-center justify-center bg-[#1AB6FF]/5">
        <BellRing size={22} className="text-[#1AB6FF]" />
      </div>
    </div>
    <p className="text-xs text-white/55 text-center">High traffic detected on Node 3</p>
  </div>
);

const AutomationCard = () => (
  <div className="absolute bottom-4 right-0 w-72 card-tech rounded-2xl p-5 float-anim" style={{ animationDelay: '2.1s' }}>
    <p className="font-display font-semibold text-white mb-4 flex items-center gap-2">
      <Bot size={18} className="text-[#1AB6FF]" /> Task Automation
    </p>
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs text-white/80">
        <span className="w-4 h-4 rounded-sm bg-[#1AB6FF] flex items-center justify-center">
          <CheckCircle2 size={10} className="text-[#04070d]" />
        </span>
        Optimize Database
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full w-3/4 bg-gradient-to-r from-[#1AB6FF] to-[#28D4FF]" />
      </div>
      <div className="flex items-center gap-2 text-xs text-white/55">
        <span className="w-4 h-4 rounded-sm border border-white/20" />
        Display Pipeline
      </div>
    </div>
  </div>
);

const ConnectingLines = () => (
  <svg className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#1AB6FF" stopOpacity="0" />
        <stop offset="50%" stopColor="#1AB6FF" stopOpacity="0.4" />
        <stop offset="100%" stopColor="#1AB6FF" stopOpacity="0" />
      </linearGradient>
    </defs>
    <path d="M 145 75 Q 280 240 145 410" stroke="url(#line-grad)" strokeWidth="1" fill="none" strokeDasharray="4 4" />
    <path d="M 430 75 Q 320 240 430 410" stroke="url(#line-grad)" strokeWidth="1" fill="none" strokeDasharray="4 4" />
  </svg>
);

const HeroRight = () => (
  <div className="relative h-[480px] hidden lg:block">
    <NodeEditorCard />
    <PerformanceCard />
    <AlertsCard />
    <AutomationCard />
    <ConnectingLines />
  </div>
);

const Hero = () => (
  <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
    <div className="absolute inset-0 bg-radial-glow pointer-events-none" />
    <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
      <HeroLeft />
      <HeroRight />
    </div>
  </section>
);

export default Hero;
